import assert from 'assert';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import isEqual from 'react-fast-compare';
import {shallowEqual} from 'react-redux';

import BenchlingConfig from 'BenchlingConfig';
import {makeReactContext} from 'lib/makeReactContext';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TABLE_EDITABLE_DEFAULT_STATE, TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {createTableModelSelector, TableModel} from 'notebook/tables/store/models/TableModel';
import {createTableEditableStore, TableEditableStore} from 'notebook/tables/store/TableStore';

interface TableContextInterface {
  useTableEditableStore: ReturnType<typeof createTableEditableStore>;
  tableModelSelector: (state: TableEditableStore) => TableModel;
  setState: (state: Partial<TableEditableState>) => void;
}

export const TableModelContext = makeReactContext<TableContextInterface>(() => {
  assert(false, 'TableContext must be used within a TableStateWrapper');
}, 'StructuredTableContext');

export const TableModelProvider = function ({
  children,
  tableProps,
}: {
  children: React.ReactNode;
  tableProps: TableEditableProps;
}): JSX.Element {
  const {table, staleRows, errorRows, warningRows, computedTable, ...stableProps} = tableProps;
  const volatileProps = useMemo(
    () => ({table, staleRows, errorRows, warningRows, computedTable}),
    [table, staleRows, errorRows, warningRows, computedTable]
  );

  // TODO(BNCH-113857): Remove this once selectors are enabled
  // tableModelRef is a temporary performance improvement while we are transitioning operations form TableEditable to
  // the selectors.  We are using a ref to store the table model so that we can update it without waiting
  // for state management system.
  const tableModelRef = useRef<TableModel>({props: tableProps, state: TABLE_EDITABLE_DEFAULT_STATE});
  useMemo(() => {
    tableModelRef.current = {
      ...tableModelRef.current,
      props: tableProps,
    };
  }, [tableProps, tableModelRef]);

  // This should explicitly only be created once, and use the initial props
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const useTableEditableStore = useMemo(() => createTableEditableStore({stableProps, volatileProps}), []);

  const {setTableState, setTableProps} = useTableEditableStore((store) => ({
    setTableState: store.setState,
    setTableProps: store.setProps,
  }));

  const tableModelSelector = useMemo(() => {
    if (BenchlingConfig.ELN_TABLE_ENABLE_STATE_SELECTORS) {
      return createTableModelSelector();
    } else {
      return () => tableModelRef.current;
    }
  }, []);

  const setState = useCallback(
    (state: Partial<TableEditableState>) => {
      const previousState = useTableEditableStore.getState().state;
      const newState = {
        ...previousState,
        ...state,
      };
      if (!isEqual(newState, previousState)) {
        tableModelRef.current = {
          ...tableModelRef.current,
          state: newState,
        };
        setTableState(newState);
      }
    },
    [setTableState, useTableEditableStore]
  );
  const contextValue: TableContextInterface = useMemo(
    () => ({tableModelSelector, setState, useTableEditableStore}),
    [tableModelSelector, setState, useTableEditableStore]
  );

  useEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (storeState.volatileProps !== volatileProps || !shallowEqual(storeState.stableProps, stableProps)) {
      setTableProps(stableProps, volatileProps);
    }
  }, [useTableEditableStore, setTableProps, volatileProps, stableProps]);

  return <TableModelContext.Provider value={contextValue}>{children}</TableModelContext.Provider>;
};
